<template>
  <el-dialog
    v-model="visible"
    :title="title"
    width="500px"
    :close-on-click-modal="false"
    @close="handleClose"
  >
    <div class="payment-modal">
      <!-- 订单信息 -->
      <div class="order-info">
        <el-descriptions :column="1" border>
          <el-descriptions-item label="订单类型">
            {{ orderTypeText }}
          </el-descriptions-item>
          <el-descriptions-item label="订单名称">
            {{ orderInfo.name }}
          </el-descriptions-item>
          <el-descriptions-item label="支付金额">
            <span class="amount">¥{{ orderInfo.amount }}</span>
          </el-descriptions-item>
        </el-descriptions>
      </div>

      <!-- 支付方式选择 -->
      <div class="payment-methods">
        <h4>选择支付方式</h4>
        <el-radio-group v-model="paymentMethod" class="method-group">
          <el-radio :label="PAYMENT_METHODS.ALIPAY" border>
            <div class="method-item">
              <el-icon :size="24"><CreditCard /></el-icon>
              <span>支付宝</span>
            </div>
          </el-radio>
          <el-radio :label="PAYMENT_METHODS.WECHAT" border>
            <div class="method-item">
              <el-icon :size="24"><Wallet /></el-icon>
              <span>微信支付</span>
            </div>
          </el-radio>
          <el-radio :label="PAYMENT_METHODS.BANK" border>
            <div class="method-item">
              <el-icon :size="24"><Postcard /></el-icon>
              <span>银行转账</span>
            </div>
          </el-radio>
        </el-radio-group>
      </div>

      <!-- 银行转账信息 -->
      <el-alert
        v-if="paymentMethod === PAYMENT_METHODS.BANK"
        type="info"
        :closable="false"
        class="bank-info"
      >
        <template #title>
          <div class="bank-details">
            <p><strong>收款账户：</strong>{{ bankInfo.accountName }}</p>
            <p><strong>开户银行：</strong>{{ bankInfo.bankName }}</p>
            <p><strong>银行账号：</strong>{{ bankInfo.accountNumber }}</p>
            <p class="tip">请在转账备注中填写您的手机号，以便核对</p>
          </div>
        </template>
      </el-alert>

      <!-- 支付说明 -->
      <el-alert
        v-if="paymentMethod === PAYMENT_METHODS.BANK"
        type="warning"
        :closable="false"
        class="payment-tip"
      >
        银行转账需要人工审核，审核通过后会员权限才会生效
      </el-alert>
    </div>

    <template #footer>
      <span class="dialog-footer">
        <el-button @click="handleClose">取消</el-button>
        <el-button 
          type="primary" 
          :loading="paying"
          @click="handleConfirm"
        >
          {{ confirmButtonText }}
        </el-button>
      </span>
    </template>
  </el-dialog>
</template>

<script>
import { CreditCard, Wallet, Postcard } from '@element-plus/icons-vue'
import { PAYMENT_METHODS, ORDER_TYPES } from '@/utils/constants'

export default {
  name: 'PaymentModal',
  
  components: {
    CreditCard,
    Wallet,
    Postcard
  },
  
  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    // 订单信息
    orderInfo: {
      type: Object,
      required: true,
      default: () => ({
        type: ORDER_TYPES.MEMBER, // 订单类型：会员/课程
        name: '', // 订单名称
        amount: 0, // 支付金额
        orderId: null // 订单ID（如果已创建）
      })
    }
  },
  
  emits: ['update:modelValue', 'confirm', 'close'],
  
  data() {
    return {
      PAYMENT_METHODS,
      ORDER_TYPES,
      paymentMethod: null, // 取消默认支付方式
      paying: false,
      // 银行转账信息（实际应从配置或API获取）
      bankInfo: {
        accountName: '深圳市心理行业协会',
        bankName: '中国工商银行深圳分行',
        accountNumber: '1234 5678 9012 3456'
      }
    }
  },
  
  computed: {
    visible: {
      get() {
        return this.modelValue
      },
      set(val) {
        this.$emit('update:modelValue', val)
      }
    },
    
    title() {
      return this.orderInfo.type === ORDER_TYPES.MEMBER ? '会员支付' : '课程支付'
    },
    
    orderTypeText() {
      return this.orderInfo.type === ORDER_TYPES.MEMBER ? '会员费用' : '课程费用'
    },
    
    confirmButtonText() {
      if (this.paymentMethod === PAYMENT_METHODS.BANK) {
        return '我已转账'
      }
      return this.paying ? '支付中...' : '确认支付'
    }
  },
  
  methods: {
    /**
     * 确认支付
     */
    async handleConfirm() {
      if (!this.paymentMethod) {
        this.$message.warning('请选择支付方式')
        return
      }
      
      this.paying = true
      
      try {
        // 触发支付事件，由父组件处理具体支付逻辑
        this.$emit('confirm', {
          paymentMethod: this.paymentMethod,
          orderInfo: this.orderInfo
        })
      } catch (error) {
        console.error('支付失败:', error)
      } finally {
        this.paying = false
      }
    },
    
    /**
     * 关闭弹窗
     */
    handleClose() {
      this.visible = false
      this.paymentMethod = null // 重置为无默认支付方式
      this.paying = false
      this.$emit('close')
    }
  }
}
</script>

<style lang="scss" scoped>
.payment-modal {
  .order-info {
    margin-bottom: 20px;
    
    .amount {
      color: #f56c6c;
      font-size: 20px;
      font-weight: bold;
    }
  }
  
  .payment-methods {
    margin-bottom: 20px;
    
    h4 {
      margin: 0 0 15px 0;
      font-size: 16px;
      color: #303133;
    }
    
    .method-group {
      width: 100%;
      display: flex;
      flex-direction: column;
      gap: 10px;
      
      .el-radio {
        margin-right: 0;
        width: 100%;
        
        &.is-bordered {
          padding: 15px 20px;
          border-radius: 4px;
          
          &.is-checked {
            border-color: #409eff;
            background-color: #ecf5ff;
          }
        }
      }
      
      .method-item {
        display: flex;
        align-items: center;
        gap: 10px;
        
        .el-icon {
          color: #409eff;
        }
        
        span {
          font-size: 16px;
        }
      }
    }
  }
  
  .bank-info {
    margin-bottom: 15px;
    
    .bank-details {
      p {
        margin: 5px 0;
        line-height: 1.8;
        
        &.tip {
          color: #e6a23c;
          margin-top: 10px;
        }
      }
    }
  }
  
  .payment-tip {
    margin-bottom: 0;
  }
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}
</style>
